// import hooks
import { useEffect } from 'react';
import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useGetDhtOptions } from '../hooks/useGetCLASettings';

// import types
import { CLA_SETTINGS_FLAG, TabProps } from '../CLASettings.types';

// import components
import withReadModeField from 'components/shared/CRUD/components/InnerPage/components/withReadModeField';
import ControlledNumericField from 'components/shared/ControlledFields/ControlledNumericField';
import MultiStepForm from 'components/shared/MultiStepForm/MultiStepForm';
import { ControlledCheckbox } from '../../../../shared';
import ControlledSelect from '../../../../shared/ControlledFields/ControlledSelect';
import Grid, { type GridProps } from '@mui/material/Grid';

// import utils
import { extraHourSchema } from '../CLASettings.schema';
import { fixedExtraHourNumberFields } from '../CLASettings.consts';

// import styles
import { StyledExtraHoursGrid } from './ExtraHoursTab.styles';
import { useGetDutyHourTypesTableData } from 'components/admin-portal/Tables/Settings/DutyHourTypes/hooks/useGetDutyHourTypesTableData';
import { DccFlag } from 'components/types';

const ExtraHoursGridContainer = (props: GridProps) => (
  <Grid
    container
    spacing={{ compact: 2, medium: 3 }}
    columns={{ compact: 4, medium: 8, large: 12 }}
    {...props}
  />
);

const ControlledNumericFieldWithReadMode = withReadModeField(
  ControlledNumericField
);
const ControlledSelectWithReadMode = withReadModeField(ControlledSelect);

const ExtraHoursTab = ({ setCurrentSchema, isEditable }: TabProps) => {
  const { t } = useTranslation('common', {
    keyPrefix: 'adminPortal.cla'
  });

  const { data: dccDropdown } = useGetDhtOptions('dutyTypeId');
  const { data: dhtOptions } = useGetDutyHourTypesTableData();

  const filteredDhtOptions =
    dhtOptions &&
    [...dhtOptions]
      .sort((a, b) => (a.description ?? '').localeCompare(b.description ?? ''))
      .filter(({ dcc }) => dcc?.flags?.includes(DccFlag.ALLOWED_WITH_NOMINAL))
      .map((item) => ({
        label: item.description ?? '',
        value: item.dutyTypeId
      }));

  useEffect(() => {
    setCurrentSchema(extraHourSchema);
  }, []);

  const { control } = useFormContext();

  return (
    <>
      <MultiStepForm.Section title={t('extraHours')}>
        <ExtraHoursGridContainer>
          {fixedExtraHourNumberFields.map((item, id) => (
            <StyledExtraHoursGrid key={id}>
              <ControlledNumericFieldWithReadMode
                key={`holidayHours_${id}`}
                variant="outlined"
                label={t(item.label)}
                name={item.name}
              />
            </StyledExtraHoursGrid>
          ))}
          <StyledExtraHoursGrid>
            <ControlledCheckbox
              control={control}
              disabled={!isEditable}
              name={`flags[${CLA_SETTINGS_FLAG.EXTRA_HOURS_NOT_ROUNDED}]`}
              label={t('extraHoursNotRounded')}
            />
          </StyledExtraHoursGrid>
          <StyledExtraHoursGrid>
            <ControlledNumericFieldWithReadMode
              variant="outlined"
              label={t('maxPayedHoursPerPeriod')}
              name={'extraMaxPeriodPaid'}
            />
          </StyledExtraHoursGrid>
        </ExtraHoursGridContainer>
      </MultiStepForm.Section>
      <MultiStepForm.Section title={t('extraHoursProcedureDutyType')}>
        <ExtraHoursGridContainer>
          <StyledExtraHoursGrid>
            <ControlledCheckbox
              disabled={!isEditable}
              control={control}
              name={`flags[${CLA_SETTINGS_FLAG.PROC_EXTRA_HOURS_NOMINAL}]`}
              label={t('extraHoursProcedure')}
            />
          </StyledExtraHoursGrid>
          <StyledExtraHoursGrid>
            <ControlledSelectWithReadMode
              control={control}
              variant="outlined"
              name={'procExtraNomDutyTypeId'}
              label={t('extraHoursProcedureDutyType')}
              options={dccDropdown ?? []}
              valueGetter="value"
              labelGetter="label"
            />
          </StyledExtraHoursGrid>
        </ExtraHoursGridContainer>
      </MultiStepForm.Section>
      <MultiStepForm.Section title={t('payOutDayLimitDutyHourType')}>
        <ExtraHoursGridContainer>
          <StyledExtraHoursGrid sx={{ minWidth: 250 }}>
            <ControlledSelectWithReadMode
              control={control}
              variant="outlined"
              name={'payOutDayLimitDhtId'}
              label={t('payOutDayLimitDutyHourType')}
              options={filteredDhtOptions ?? []}
              valueGetter="value"
              labelGetter="label"
            />
          </StyledExtraHoursGrid>
        </ExtraHoursGridContainer>
      </MultiStepForm.Section>
    </>
  );
};

export default ExtraHoursTab;
